import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * AgentDemo — the interactive "what the agent actually does" demo on the landing page.
 *
 * A visitor picks one of three real BM situations and watches the agent execute:
 * each step lands one after another, then the result cards appear.
 *
 * Two rules keep this honest rather than a sales cartoon:
 *   1. It mirrors the real product — same steps, same cards, same wording as the
 *      in-app copilot panel. What you see here is what you get.
 *   2. It always ends on the confirmation gate, because that's a real property of
 *      the product: nothing outbound leaves without a human click.
 *
 * Everything is scripted client-side — no AI call from a public page.
 */

const SCENARIOS = [
  {
    id: 'rdv',
    tab: 'Retour de RDV',
    prompt:
      "Je reviens d'un rendez-vous client : il recherche un Data Engineer (démarrage le 25/11, "
      + "mission de 6 mois, TJM 650-700 €, 3 jours de télétravail). Crée le besoin, trouve les "
      + "candidats et prépare les emails.",
    steps: [
      'Analyse de la demande',
      'Création du besoin « Data Engineer »',
      'Recherche dans le vivier',
      'Rédaction des emails',
    ],
    cards: [
      {
        kind: 'besoin',
        title: 'Besoin créé',
        badge: 'Enregistré',
        rows: [
          ['Poste', 'Data Engineer'],
          ['Démarrage', '25/11/2026'],
          ['Durée', '6 mois'],
          ['TJM', '650 – 700 €'],
        ],
        chips: ['Python', 'Spark', 'SQL', 'Airflow'],
      },
      {
        kind: 'candidats',
        title: 'Candidats proposés',
        badge: '3 trouvés',
        people: [
          { name: 'Karim Benali', role: 'Data Engineer Senior · 8 ans', score: 96 },
          { name: 'Léa Fontaine', role: 'Data Engineer · 6 ans', score: 92 },
          { name: 'Marc Oliveira', role: 'Lead Data Engineer · 9 ans', score: 89 },
        ],
      },
      { kind: 'gate', label: '3 emails prêts à envoyer' },
    ],
  },
  {
    id: 'relance',
    tab: 'Relancer',
    prompt: 'Qui n\'a pas répondu depuis la semaine dernière ? Prépare-leur une relance.',
    steps: [
      'Lecture du journal d\'envois',
      'Repérage des profils sans réponse',
      'Rédaction des relances',
    ],
    cards: [
      {
        kind: 'candidats',
        title: 'Sans réponse depuis 7 jours',
        badge: '2 profils',
        people: [
          { name: 'Sofia Nabil', role: 'Sondée le 17/11 · mission BNP', score: null, tag: '7 j' },
          { name: 'Thomas Rey', role: 'Sondé le 16/11 · mission Orange', score: null, tag: '8 j' },
        ],
      },
      { kind: 'gate', label: '2 relances prêtes à envoyer' },
    ],
  },
  {
    id: 'compte',
    tab: 'Ouvrir un compte',
    prompt: 'Crée le compte grand compte BNP Paribas, entité CIB, équipe Data & Analytics.',
    steps: [
      'Création du compte client',
      'Rattachement de l\'entité',
      'Création de l\'équipe',
    ],
    cards: [
      {
        kind: 'tree',
        title: 'Compte créé',
        badge: 'Enregistré',
        nodes: [
          { level: 0, label: 'BNP Paribas', meta: 'Grand compte' },
          { level: 1, label: 'CIB', meta: 'Entité' },
          { level: 2, label: 'Data & Analytics', meta: 'Équipe' },
        ],
      },
    ],
  },
];

const STEP_MS = 620;
const CARD_MS = 420;

const initials = (n) => n.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

export default function AgentDemo() {
  const [active, setActive] = useState(0);
  const [stepsDone, setStepsDone] = useState(0);
  const [cardsShown, setCardsShown] = useState(0);
  const [running, setRunning] = useState(false);
  const timers = useRef([]);

  const scenario = SCENARIOS[active];

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const play = useCallback((idx) => {
    clearTimers();
    const s = SCENARIOS[idx];
    setStepsDone(0);
    setCardsShown(0);
    setRunning(true);

    // Respect the visitor's motion preference: show the finished result at once.
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setStepsDone(s.steps.length);
      setCardsShown(s.cards.length);
      setRunning(false);
      return;
    }

    s.steps.forEach((_, i) => {
      timers.current.push(setTimeout(() => setStepsDone(i + 1), STEP_MS * (i + 1)));
    });
    const afterSteps = STEP_MS * (s.steps.length + 1);
    s.cards.forEach((_, i) => {
      timers.current.push(setTimeout(() => setCardsShown(i + 1), afterSteps + CARD_MS * i));
    });
    timers.current.push(setTimeout(
      () => setRunning(false),
      afterSteps + CARD_MS * s.cards.length,
    ));
  }, []);

  // Play the first scenario once the section scrolls into view — an animation
  // that fired while off-screen would be missed entirely.
  const rootRef = useRef(null);
  const startedRef = useRef(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { play(0); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !startedRef.current) {
        startedRef.current = true;
        play(0);
      }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [play]);

  useEffect(() => clearTimers, []);

  const select = (i) => {
    setActive(i);
    startedRef.current = true;
    play(i);
  };

  return (
    <div className="agd" ref={rootRef}>
      <style>{CSS}</style>

      <div className="agd-tabs" role="tablist" aria-label="Scénarios de démonstration">
        {SCENARIOS.map((s, i) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={i === active}
            className={`agd-tab${i === active ? ' on' : ''}`}
            onClick={() => select(i)}
          >
            {s.tab}
          </button>
        ))}
      </div>

      <div className="agd-win">
        <div className="agd-bar">
          <span className="agd-tl r" /><span className="agd-tl y" /><span className="agd-tl g" />
          <span className="agd-url">app.skilldeck.io</span>
          <span className="agd-live">Agent · bêta</span>
        </div>

        <div className="agd-body">
          <div className="agd-ask">
            <span className="agd-you">BM</span>
            <p>{scenario.prompt}</p>
          </div>

          <ol className="agd-steps">
            {scenario.steps.map((label, i) => {
              const done = i < stepsDone;
              const current = i === stepsDone && running;
              return (
                <li key={label} className={done ? 'done' : current ? 'now' : ''}>
                  <span className="agd-dot">{done ? '✓' : current ? '' : ''}</span>
                  <span className="agd-step-label">{label}</span>
                </li>
              );
            })}
          </ol>

          <div className="agd-cards">
            {scenario.cards.slice(0, cardsShown).map((c, i) => (
              <Card key={i} card={c} />
            ))}
          </div>
        </div>

        <div className="agd-foot">
          <span className="agd-note">🔒 L'agent prépare — vous validez.</span>
          <button className="agd-replay" onClick={() => play(active)} disabled={running}>
            {running ? 'En cours…' : '↺ Rejouer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({ card }) {
  if (card.kind === 'gate') {
    return (
      <div className="agd-card agd-gate">
        <div className="agd-gate-txt">
          <b>{card.label}</b>
          <span>Rien ne part sans votre confirmation.</span>
        </div>
        <span className="agd-gate-btn">Confirmer l'envoi →</span>
      </div>
    );
  }

  return (
    <div className="agd-card">
      <div className="agd-card-h">
        {card.title}
        {card.badge && <span className="agd-badge">{card.badge}</span>}
      </div>

      {card.rows && (
        <div className="agd-grid">
          {card.rows.map(([k, v]) => (
            <div className="agd-cell" key={k}>
              <span className="agd-k">{k}</span>
              <span className="agd-v">{v}</span>
            </div>
          ))}
        </div>
      )}

      {card.chips && (
        <div className="agd-chips">
          {card.chips.map((c) => <span className="agd-chip" key={c}>{c}</span>)}
        </div>
      )}

      {card.people && card.people.map((p) => (
        <div className="agd-person" key={p.name}>
          <span className="agd-av">{initials(p.name)}</span>
          <span className="agd-person-main">
            <b>{p.name}</b>
            <em>{p.role}</em>
          </span>
          {p.score != null
            ? <span className="agd-score">{p.score}<i>match</i></span>
            : <span className="agd-tag">{p.tag}</span>}
        </div>
      ))}

      {card.nodes && (
        <div className="agd-tree">
          {card.nodes.map((n) => (
            <div className="agd-node" key={n.label} style={{ marginLeft: n.level * 22 }}>
              <span className="agd-node-dot" />
              <b>{n.label}</b>
              <em>{n.meta}</em>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CSS = `
.agd { max-width: 780px; margin: 0 auto; }

.agd-tabs { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }
.agd-tab {
  font-family: inherit; font-size: .82rem; font-weight: 600; cursor: pointer;
  padding: 8px 16px; border-radius: 100px;
  background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.08);
  color: #7a75a0; transition: all .16s;
}
.agd-tab:hover { color: #f0eeff; border-color: rgba(108,71,255,.4); }
.agd-tab.on { background: rgba(108,71,255,.14); border-color: rgba(108,71,255,.45); color: #c4b5fd; }
.agd-tab:focus-visible { outline: 2px solid #a78bfa; outline-offset: 2px; }

.agd-win {
  background: #080818; border: 1px solid rgba(255,255,255,.08); border-radius: 16px;
  overflow: hidden; box-shadow: 0 30px 80px -30px rgba(0,0,0,.9);
}
.agd-bar {
  display: flex; align-items: center; gap: 7px; padding: 11px 15px;
  border-bottom: 1px solid rgba(255,255,255,.06); background: rgba(255,255,255,.02);
}
.agd-tl { width: 9px; height: 9px; border-radius: 50%; }
.agd-tl.r { background: #ff5f57; } .agd-tl.y { background: #febc2e; } .agd-tl.g { background: #28c840; }
.agd-url { flex: 1; text-align: center; font-size: .72rem; color: #5a5878; }
.agd-live {
  font-size: .66rem; font-weight: 700; letter-spacing: .4px; color: #c4b5fd;
  background: rgba(108,71,255,.16); border: 1px solid rgba(108,71,255,.3);
  padding: 3px 9px; border-radius: 100px;
}

.agd-body { padding: 20px; min-height: 340px; }

.agd-ask { display: flex; gap: 11px; margin-bottom: 18px; }
.agd-you {
  width: 27px; height: 27px; flex: none; border-radius: 8px; display: grid; place-items: center;
  font-size: .65rem; font-weight: 800; color: #a099e8;
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.09);
}
.agd-ask p {
  flex: 1; background: rgba(108,71,255,.1); border: 1px solid rgba(108,71,255,.24);
  border-radius: 4px 12px 12px 12px; padding: 11px 14px;
  font-size: .87rem; color: #e4e0ff; line-height: 1.55;
}

.agd-steps { list-style: none; display: flex; flex-direction: column; gap: 2px; margin-bottom: 16px; }
.agd-steps li {
  display: flex; align-items: center; gap: 10px; padding: 6px 2px;
  font-size: .84rem; color: #45435e; transition: color .3s;
}
.agd-steps li.now { color: #a78bfa; }
.agd-steps li.done { color: #8e88b5; }
.agd-dot {
  width: 18px; height: 18px; flex: none; border-radius: 50%; display: grid; place-items: center;
  font-size: .6rem; font-weight: 800;
  border: 1.5px solid currentColor; opacity: .5; transition: all .3s;
}
.agd-steps li.done .agd-dot { opacity: 1; background: rgba(52,211,153,.15); border-color: transparent; color: #34d399; }
.agd-steps li.now .agd-dot { opacity: 1; animation: agdPulse 1s ease-in-out infinite; }
@keyframes agdPulse { 0%,100% { transform: scale(1); opacity: .6; } 50% { transform: scale(1.18); opacity: 1; } }

.agd-cards { display: flex; flex-direction: column; gap: 10px; }
.agd-card {
  background: rgba(255,255,255,.025); border: 1px solid rgba(255,255,255,.07);
  border-radius: 12px; overflow: hidden;
  animation: agdIn .34s cubic-bezier(.2,.7,.2,1) both;
}
@keyframes agdIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.agd-card-h {
  display: flex; align-items: center; padding: 10px 14px;
  border-bottom: 1px solid rgba(255,255,255,.06);
  font-size: .74rem; font-weight: 700; color: #8e88b5; letter-spacing: .2px;
}
.agd-badge {
  margin-left: auto; font-size: .64rem; font-weight: 700; padding: 3px 9px; border-radius: 100px;
  background: rgba(52,211,153,.14); color: #34d399;
}

.agd-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: rgba(255,255,255,.06); }
.agd-cell { background: #0a0a1a; padding: 9px 14px; display: flex; flex-direction: column; gap: 2px; }
.agd-k { font-size: .6rem; text-transform: uppercase; letter-spacing: .5px; color: #45435e; font-weight: 700; }
.agd-v { font-size: .82rem; font-weight: 600; color: #f0eeff; font-variant-numeric: tabular-nums; }

.agd-chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 11px 14px; }
.agd-chip {
  font-size: .68rem; font-weight: 600; padding: 4px 10px; border-radius: 7px;
  background: rgba(108,71,255,.12); color: #c4b5fd; border: 1px solid rgba(108,71,255,.22);
}

.agd-person { display: flex; align-items: center; gap: 11px; padding: 10px 14px; border-top: 1px solid rgba(255,255,255,.05); }
.agd-person:first-of-type { border-top: none; }
.agd-av {
  width: 30px; height: 30px; flex: none; border-radius: 9px; display: grid; place-items: center;
  font-size: .68rem; font-weight: 700; color: #8e88b5;
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.08);
}
.agd-person-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.agd-person-main b { font-size: .82rem; font-weight: 700; color: #f0eeff; }
.agd-person-main em { font-style: normal; font-size: .72rem; color: #5a5878; margin-top: 1px; }
.agd-score { font-size: 1.02rem; font-weight: 800; color: #a78bfa; text-align: right; font-variant-numeric: tabular-nums; }
.agd-score i { display: block; font-style: normal; font-size: .55rem; text-transform: uppercase; letter-spacing: .5px; color: #45435e; font-weight: 700; }
.agd-tag { font-size: .68rem; font-weight: 700; color: #fbbf24; background: rgba(251,191,36,.13); padding: 3px 9px; border-radius: 7px; }

.agd-tree { padding: 13px 14px; display: flex; flex-direction: column; gap: 8px; }
.agd-node { display: flex; align-items: center; gap: 9px; }
.agd-node-dot { width: 6px; height: 6px; border-radius: 50%; background: #6c47ff; flex: none; }
.agd-node b { font-size: .82rem; color: #f0eeff; font-weight: 700; }
.agd-node em { font-style: normal; font-size: .68rem; color: #45435e; }

.agd-gate { display: flex; align-items: center; gap: 12px; padding: 13px 14px; flex-wrap: wrap; background: rgba(108,71,255,.06); }
.agd-gate-txt { flex: 1; min-width: 160px; display: flex; flex-direction: column; }
.agd-gate-txt b { font-size: .82rem; color: #f0eeff; font-weight: 700; }
.agd-gate-txt span { font-size: .7rem; color: #5a5878; margin-top: 2px; }
.agd-gate-btn {
  font-size: .76rem; font-weight: 700; color: #fff; padding: 9px 15px; border-radius: 9px;
  background: linear-gradient(135deg, #8b7cf0, #6c47ff);
  box-shadow: 0 6px 18px -6px rgba(108,71,255,.8);
}

.agd-foot {
  display: flex; align-items: center; gap: 10px; padding: 11px 15px;
  border-top: 1px solid rgba(255,255,255,.06); background: rgba(255,255,255,.015);
}
.agd-note { flex: 1; font-size: .7rem; color: #5a5878; }
.agd-replay {
  font-family: inherit; font-size: .72rem; font-weight: 700; cursor: pointer;
  padding: 7px 13px; border-radius: 8px; color: #8e88b5;
  background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.09);
}
.agd-replay:hover:not(:disabled) { color: #f0eeff; border-color: rgba(108,71,255,.4); }
.agd-replay:disabled { opacity: .45; cursor: default; }
.agd-replay:focus-visible { outline: 2px solid #a78bfa; outline-offset: 2px; }

@media (max-width: 560px) {
  .agd-grid { grid-template-columns: 1fr; }
  .agd-body { padding: 15px; min-height: 300px; }
}
@media (prefers-reduced-motion: reduce) {
  .agd-card { animation: none; }
  .agd-steps li.now .agd-dot { animation: none; }
}
`;
